import React, { useEffect, useRef, useState } from 'react';
import { Heart, Leaf, Award, TrendingUp, Users, Clock } from 'lucide-react';

const About: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Heart className="text-red-500" size={32} />,
      title: "Tradição Familiar",
      description: "Mais de 70 anos de experiência passada de geração em geração"
    },
    {
      icon: <Leaf className="text-green-500" size={32} />,
      title: "Produtos Frescos",
      description: "Seleção rigorosa e cuidadosa de hortaliças da mais alta qualidade"
    },
    {
      icon: <Award className="text-yellow-500" size={32} />,
      title: "Excelência",
      description: "Reconhecimento no mercado por nossa dedicação e qualidade"
    },
    {
      icon: <TrendingUp className="text-blue-500" size={32} />,
      title: "Crescimento",
      description: "Expansão constante atendendo novas regiões e clientes"
    },
    {
      icon: <Users className="text-purple-500" size={32} />,
      title: "Equipe Especializada",
      description: "Profissionais qualificados comprometidos com a excelência"
    },
    {
      icon: <Clock className="text-indigo-500" size={32} />,
      title: "Atendimento 24h",
      description: "Suporte completo para atender suas necessidades a qualquer hora"
    }
  ];

  return (
    <section ref={sectionRef} id="about" className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container-custom">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'scroll-reveal visible' : 'scroll-reveal'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mb-4">
            Nossa <span className="text-gradient">História</span>
          </h2>
          <p className="text-xl text-primary-700 max-w-3xl mx-auto">
            Conheça a trajetória de uma empresa que há décadas cultiva confiança e qualidade no setor de hortaliças
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Text Content */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'scroll-reveal-left visible' : 'scroll-reveal-left'}`}>
            <h3 className="text-3xl font-bold text-primary-800 mb-6">
              Mais de 70 Anos de Tradição
            </h3>
            <div className="space-y-6 text-lg text-primary-700 leading-relaxed">
              <p>
                A <strong className="text-primary-600">Sol Nascente Hortaliças</strong> nasceu do sonho de uma família apaixonada pela agricultura e pela qualidade dos produtos naturais. Desde nossa fundação, nos dedicamos a fornecer hortaliças frescas e selecionadas para nossos clientes.
              </p>
              <p>
                Com mais de <strong className="text-primary-600">70 anos de experiência</strong>, construímos uma reputação sólida baseada na confiança, qualidade e excelência no atendimento. Nossa sede está localizada em <strong className="text-primary-600">Ibiúna-SP</strong>, de onde distribuímos nossos produtos para toda a Grande São Paulo.
              </p>
              <p>
                Hoje, atendemos mais de <strong className="text-primary-600">20 regiões</strong> diferentes na Grande São Paulo, sempre mantendo o compromisso com a qualidade que nos tornou referência no setor de distribuição de hortaliças. Não possuímos filiais, trabalhamos com distribuição direta do produtor.
              </p>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-600 mb-2">70+</div>
                <div className="text-sm text-primary-600">Anos de Experiência</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-600 mb-2">20+</div>
                <div className="text-sm text-primary-600">Regiões Atendidas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-600 mb-2">100%</div>
                <div className="text-sm text-primary-600">Qualidade Garantida</div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className={`relative transition-all duration-1000 delay-400 ${isVisible ? 'scroll-reveal-right visible' : 'scroll-reveal-right'}`}>
            <div className="relative">
              <img 
                src="/img/carrosel/imagem1.jpeg" 
                alt="Sol Nascente Hortaliças - Tradição e Qualidade"
                className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                onError={(e) => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1542838132-92c53300491e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-3xl"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <h4 className="text-xl font-bold mb-2">Qualidade em Cada Produto</h4>
                <p className="text-sm opacity-90">Seleção cuidadosa para garantir o melhor</p>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center floating-element">
              <Leaf className="text-primary-600" size={32} />
            </div>
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center floating-delayed">
              <Award className="text-secondary-600" size={24} />
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`bg-white p-8 rounded-2xl shadow-lg card-hover transition-all duration-1000 delay-${(index + 1) * 100} ${isVisible ? 'scroll-scale visible' : 'scroll-scale'}`}
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-gray-50 rounded-2xl mr-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-primary-800">{feature.title}</h3>
              </div>
              <p className="text-primary-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-800 ${isVisible ? 'scroll-reveal visible' : 'scroll-reveal'}`}>
          <h3 className="text-3xl font-bold text-primary-800 mb-6">
            Pronto para Conhecer Nossos Produtos?
          </h3>
          <p className="text-xl text-primary-600 mb-8 max-w-2xl mx-auto">
            Descubra a qualidade que nos tornou referência em hortaliças há mais de 70 anos
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/551532415413" 
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Entre em Contato
            </a>
            <button 
              onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-outline"
            >
              Ver Produtos
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
